<script setup lang="ts">
import { computed, watch } from 'vue';
import MxIcon from './MxIcon.vue';
import MxButton from './MxButton.vue';
import { backButton } from '@/composables/useTelegramWebApp';
import type { Template } from '@/lib/templates';

const props = defineProps<{ template: Template | null; open: boolean }>();
const emit = defineEmits<{
  (e: 'update:open', v: boolean): void;
  (e: 'select', t: Template): void;
}>();

const visible = computed(() => props.open && props.template !== null);

function close() { emit('update:open', false); }
function pick() { if (props.template) emit('select', props.template); }

watch(visible, (v) => {
  if (v) backButton.show(close);
  else backButton.hide();
});
</script>

<template>
  <Transition name="modal">
    <div v-if="visible" class="modal" role="dialog" aria-modal="true">
      <button class="modal__close" data-test="close" @click="close">
        <MxIcon name="X" :size="20" />
      </button>

      <div class="modal__scroll">
        <h1 class="modal__title">{{ template!.title }}</h1>
        <p class="modal__descr">{{ template!.description }}</p>

        <div :class="['modal__grid', { 'modal__grid--single': template!.examples.length <= 1 }]">
          <div v-for="(ex, i) in template!.examples" :key="i" class="modal__cell">
            <video
              v-if="ex.url.endsWith('.mp4')"
              :src="ex.url"
              :poster="ex.poster"
              muted loop autoplay playsinline preload="metadata"
            />
            <img v-else :src="ex.url" :alt="`example ${i}`" loading="lazy" />
          </div>
        </div>
      </div>

      <div class="modal__cta">
        <MxButton variant="primary" data-test="cta" @click="pick">
          ✦ Сгенерировать
        </MxButton>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.modal {
  position: fixed;
  inset: 0;
  z-index: 100;
  background: var(--bg-base);
  display: flex;
  flex-direction: column;
}
.modal__close {
  position: absolute;
  top: calc(env(safe-area-inset-top) + 8px);
  right: 12px;
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: var(--bg-overlay);
  color: var(--text-primary);
  z-index: 1;
}
.modal__scroll {
  flex: 1;
  overflow-y: auto;
  padding: calc(env(safe-area-inset-top) + 56px) var(--sp-4) 100px;
}
.modal__title {
  font-family: var(--font-display);
  font-size: var(--fs-2xl);
  font-weight: 700;
  letter-spacing: -0.02em;
  text-transform: uppercase;
  margin-bottom: var(--sp-2);
}
.modal__descr {
  color: var(--text-secondary);
  margin-bottom: var(--sp-5);
}
.modal__grid {
  column-count: 2;
  column-gap: var(--sp-3);
}
.modal__grid--single {
  /* One example: take full width, centered, capped so vertical clips don't dominate */
  column-count: 1;
  display: flex;
  justify-content: center;
}
.modal__grid--single .modal__cell {
  width: 100%;
  max-width: 320px;
}
.modal__cell {
  break-inside: avoid;
  margin-bottom: var(--sp-3);
  border-radius: var(--r-md);
  overflow: hidden;
  background: var(--bg-elevated);
}
.modal__cell video, .modal__cell img {
  width: 100%;
  height: auto;
  display: block;
}
.modal__cta {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: var(--sp-3) var(--sp-4) calc(var(--sp-3) + env(safe-area-inset-bottom));
  background: linear-gradient(transparent, var(--bg-base) 30%);
}
.modal__cta button { width: 100%; }
.modal-enter-active, .modal-leave-active { transition: opacity 0.18s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
</style>
